import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PublicRoute } from '@/components/PublicRoute';
import { Toast } from '@/components/Toast';
import { InactivityProvider } from '@/components/InactivityProvider';
import { useTheme } from '@/hooks/useTheme';

// Critical routes - loaded immediately
import { Login } from '@/pages/Login';
import { ForgotPasswordScreen } from '@/pages/ForgotPasswordScreen';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { Dashboard } from '@/pages/Dashboard';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';

// Lazy load all other routes for code splitting
const TechnicalDashboard = lazy(() => import('@/pages/technical/TechnicalDashboard').then(m => ({ default: m.TechnicalDashboard })));
const FieldOperationsScreen = lazy(() => import('@/pages/technical/FieldOperationsScreen').then(m => ({ default: m.FieldOperationsScreen })));
const CustomerSupportScreen = lazy(() => import('@/pages/technical/CustomerSupportScreen').then(m => ({ default: m.CustomerSupportScreen })));
const SupportTicketsPage = lazy(() => import('@/pages/technical/SupportTicketsPage').then(m => ({ default: m.SupportTicketsPage })));
const ActivationsRequestPage = lazy(() => import('@/pages/technical/ActivationsRequestPage').then(m => ({ default: m.ActivationsRequestPage })));
const SchedulingDashboard = lazy(() => import('@/pages/technical/SchedulingDashboard').then(m => ({ default: m.SchedulingDashboard })));
const ScheduleCalendarPage = lazy(() => import('@/pages/technical/ScheduleCalendarPage').then(m => ({ default: m.ScheduleCalendarPage })));
const ScheduleReportsPage = lazy(() => import('@/pages/technical/ScheduleReportsPage').then(m => ({ default: m.ScheduleReportsPage })));
const TimersPage = lazy(() => import('@/pages/technical/TimersPage').then(m => ({ default: m.TimersPage })));
const SalesDashboard = lazy(() => import('@/pages/sales/SalesDashboard').then(m => ({ default: m.SalesDashboard })));
const SalesSiteVisitsScreen = lazy(() => import('@/pages/sales/SiteVisitsScreen').then(m => ({ default: m.SiteVisitsScreen })));
const SalesScheduleJobsScreen = lazy(() => import('@/pages/sales/ScheduleJobsScreen').then(m => ({ default: m.ScheduleJobsScreen })));
const QuotationsScreen = lazy(() => import('@/pages/sales/QuotationsScreen').then(m => ({ default: m.QuotationsScreen })));
const CreateQuotationScreen = lazy(() => import('@/pages/sales/CreateQuotationScreen').then(m => ({ default: m.CreateQuotationScreen })));
const AccountsDashboard = lazy(() => import('@/pages/accounts/AccountsDashboard'));
const AccountsRecords = lazy(() => import('@/pages/accounts/AccountsRecords'));
const KitCollectionForm = lazy(() => import('@/pages/accounts/KitCollectionForm'));
const KitReturnFormScreen = lazy(() => import('@/pages/accounts/KitReturnFormScreen'));
const SignatureCapture = lazy(() => import('@/pages/accounts/SignatureCapture'));
const InventoryForm = lazy(() => import('@/pages/accounts/InventoryForm'));
const OperationsActivations = lazy(() => import('@/pages/accounts/OperationsActivations'));
const AccountsSiteVisitsScreen = lazy(() => import('@/pages/accounts/SiteVisitsScreen'));
const SiteVisitDetailScreen = lazy(() => import('@/pages/accounts/SiteVisitDetailScreen'));
const AccountsScheduleJobsScreen = lazy(() => import('@/pages/accounts/ScheduleJobsScreen'));
const ScheduleJobDetailScreen = lazy(() => import('@/pages/accounts/ScheduleJobDetailScreen'));
const PackagesSalesReport = lazy(() => import('@/pages/accounts/PackagesSalesReport'));
const AddCustomer = lazy(() => import('@/pages/accounts/AddCustomer'));
const BankRecords = lazy(() => import('@/pages/accounts/BankRecords'));
const CashRecords = lazy(() => import('@/pages/accounts/CashRecords'));
const HRDashboard = lazy(() => import('@/pages/hr/HRDashboard').then(m => ({ default: m.HRDashboard })));
const LeaveRequestForm = lazy(() => import('@/pages/hr/LeaveRequestForm').then(m => ({ default: m.LeaveRequestForm })));
const MyLeaveRequests = lazy(() => import('@/pages/hr/MyLeaveRequests').then(m => ({ default: m.MyLeaveRequests })));
const HRApprovals = lazy(() => import('@/pages/hr/HRApprovals').then(m => ({ default: m.HRApprovals })));
const DepartmentApprovals = lazy(() => import('@/pages/hr/DepartmentApprovals').then(m => ({ default: m.DepartmentApprovals })));
const AllLeaveRecords = lazy(() => import('@/pages/hr/AllLeaveRecords').then(m => ({ default: m.AllLeaveRecords })));
const CEOPanel = lazy(() => import('@/pages/hr/CEOPanel').then(m => ({ default: m.CEOPanel })));
const LibraryDashboard = lazy(() => import('@/pages/library/LibraryDashboard').then(m => ({ default: m.LibraryDashboard })));
const PhysicalBooksScreen = lazy(() => import('@/pages/library/PhysicalBooksScreen').then(m => ({ default: m.PhysicalBooksScreen })));
const BookDetailScreen = lazy(() => import('@/pages/library/BookDetailScreen').then(m => ({ default: m.BookDetailScreen })));
const MyBorrowsScreen = lazy(() => import('@/pages/library/MyBorrowsScreen').then(m => ({ default: m.MyBorrowsScreen })));
const ManageBooksScreen = lazy(() => import('@/pages/library/ManageBooksScreen').then(m => ({ default: m.ManageBooksScreen })));
const ManageCategoriesScreen = lazy(() => import('@/pages/library/ManageCategoriesScreen').then(m => ({ default: m.ManageCategoriesScreen })));
const BorrowRequestsScreen = lazy(() => import('@/pages/library/BorrowRequestsScreen').then(m => ({ default: m.BorrowRequestsScreen })));
const AllRecordsScreen = lazy(() => import('@/pages/library/AllRecordsScreen').then(m => ({ default: m.AllRecordsScreen })));
const LibrarySettingsScreen = lazy(() => import('@/pages/library/LibrarySettingsScreen').then(m => ({ default: m.LibrarySettingsScreen })));
const LibraryReportsScreen = lazy(() => import('@/pages/library/LibraryReportsScreen').then(m => ({ default: m.LibraryReportsScreen })));
const VehiclesDashboard = lazy(() => import('@/pages/vehicles/VehiclesDashboard').then(m => ({ default: m.VehiclesDashboard })));
const UsersListPage = lazy(() => import('@/pages/users/UsersListPage').then(m => ({ default: m.UsersListPage })));
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage').then(m => ({ default: m.UserDetailPage })));
const CreateUserPage = lazy(() => import('@/pages/users/CreateUserPage').then(m => ({ default: m.CreateUserPage })));
const EditUserPage = lazy(() => import('@/pages/users/EditUserPage').then(m => ({ default: m.EditUserPage })));
const ProfileDashboard = lazy(() => import('@/pages/profile/ProfileDashboard').then(m => ({ default: m.ProfileDashboard })));
const ReportsDashboard = lazy(() => import('@/pages/reports/ReportsDashboard').then(m => ({ default: m.ReportsDashboard })));
const TargetDashboard = lazy(() => import('@/pages/target/TargetDashboard').then(m => ({ default: m.TargetDashboard })));
const NewsletterDashboard = lazy(() => import('@/pages/newsletter/NewsletterDashboard').then(m => ({ default: m.NewsletterDashboard })));
const NewsletterDetailPage = lazy(() => import('@/pages/newsletter/NewsletterDetailPage').then(m => ({ default: m.NewsletterDetailPage })));
const CreateNewsletterPage = lazy(() => import('@/pages/newsletter/CreateNewsletterPage').then(m => ({ default: m.CreateNewsletterPage })));
const RequisitionsDashboard = lazy(() => import('@/pages/requisitions/RequisitionsDashboard').then(m => ({ default: m.RequisitionsDashboard })));
const AnniversaryHustlePage = lazy(() => import('@/pages/anniversary/AnniversaryHustlePage').then(m => ({ default: m.AnniversaryHustlePage })));
const RequisitionsList = lazy(() => import('@/pages/requisitions/RequisitionsList').then(m => ({ default: m.RequisitionsList })));
const RequisitionDetail = lazy(() => import('@/pages/requisitions/RequisitionDetail').then(m => ({ default: m.RequisitionDetail })));
const RequisitionWorkflowDetail = lazy(() => import('@/pages/requisitions/RequisitionWorkflowDetail').then(m => ({ default: m.RequisitionWorkflowDetail })));

// Create a client with optimized caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnReconnect: false,
    },
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Helper component to wrap lazy routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

const App: React.FC = () => {
  const { theme } = useTheme();

  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <InactivityProvider>
          <div className={`min-h-screen ${theme.mode === 'dark' ? 'dark' : ''}`}>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordScreen />
                </PublicRoute>
              }
            />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/anniversary-hustle"
              element={
                <ProtectedRoute>
                  <LazyRoute><AnniversaryHustlePage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            
            {/* Department Dashboard Routes */}
            <Route
              path="/technical"
              element={
                <ProtectedRoute>
                  <LazyRoute><TechnicalDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/field-operations"
              element={
                <ProtectedRoute>
                  <LazyRoute><FieldOperationsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/customer-support"
              element={
                <ProtectedRoute>
                  <LazyRoute><CustomerSupportScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/support-tickets"
              element={
                <ProtectedRoute>
                  <LazyRoute><SupportTicketsPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/activations-request"
              element={
                <ProtectedRoute>
                  <LazyRoute><ActivationsRequestPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/scheduling"
              element={
                <ProtectedRoute>
                  <LazyRoute><SchedulingDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/schedule-calendar"
              element={
                <ProtectedRoute>
                  <LazyRoute><ScheduleCalendarPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/scheduling/reports"
              element={
                <ProtectedRoute>
                  <LazyRoute><ScheduleReportsPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/technical/scheduling/timers"
              element={
                <ProtectedRoute>
                  <LazyRoute><TimersPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <LazyRoute><SalesDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/site-visits"
              element={
                <ProtectedRoute>
                  <LazyRoute><SalesSiteVisitsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/schedule-jobs"
              element={
                <ProtectedRoute>
                  <LazyRoute><SalesScheduleJobsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/quotations"
              element={
                <ProtectedRoute>
                  <LazyRoute><QuotationsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/quotations/create"
              element={
                <ProtectedRoute>
                  <LazyRoute><CreateQuotationScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <LazyRoute><AccountsDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/records"
              element={
                <ProtectedRoute>
                  <LazyRoute><AccountsRecords /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/kit-collection-form"
              element={
                <ProtectedRoute>
                  <LazyRoute><KitCollectionForm /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/kit-return-form"
              element={
                <ProtectedRoute>
                  <LazyRoute><KitReturnFormScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/signature-capture"
              element={
                <ProtectedRoute>
                  <LazyRoute><SignatureCapture /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/inventory-form"
              element={
                <ProtectedRoute>
                  <LazyRoute><InventoryForm /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/operations-activation-form"
              element={
                <ProtectedRoute>
                  <LazyRoute><OperationsActivations /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/site-visits"
              element={
                <ProtectedRoute>
                  <LazyRoute><AccountsSiteVisitsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/site-visits/:visitId"
              element={
                <ProtectedRoute>
                  <LazyRoute><SiteVisitDetailScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/schedule-jobs"
              element={
                <ProtectedRoute>
                  <LazyRoute><AccountsScheduleJobsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/schedule-jobs/:jobId"
              element={
                <ProtectedRoute>
                  <LazyRoute><ScheduleJobDetailScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/packages-sales-report"
              element={
                <ProtectedRoute>
                  <LazyRoute><PackagesSalesReport /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/add-customer"
              element={
                <ProtectedRoute>
                  <LazyRoute><AddCustomer /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/bank-records"
              element={
                <ProtectedRoute>
                  <LazyRoute><BankRecords /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/cash-records"
              element={
                <ProtectedRoute>
                  <LazyRoute><CashRecords /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionsDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions/list"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionsList /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions/all"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionsList /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions/hod-approval"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionsList /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions/admin-process"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionsList /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions/:id"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionDetail /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/requisitions/workflow/:id"
              element={
                <ProtectedRoute>
                  <LazyRoute><RequisitionWorkflowDetail /></LazyRoute>
                </ProtectedRoute>
              }
            />
                    <Route
                      path="/hr"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><HRDashboard /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/leave-request"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><LeaveRequestForm /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/my-requests"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><MyLeaveRequests /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/approvals"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><HRApprovals /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/department-approvals"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><DepartmentApprovals /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/all-records"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><AllLeaveRecords /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/ceo-panel"
                      element={
                        <ProtectedRoute>
                          <LazyRoute><CEOPanel /></LazyRoute>
                        </ProtectedRoute>
                      }
                    />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <LazyRoute><LibraryDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/physical-books"
              element={
                <ProtectedRoute>
                  <LazyRoute><PhysicalBooksScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/book/:bookId"
              element={
                <ProtectedRoute>
                  <LazyRoute><BookDetailScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/my-borrows"
              element={
                <ProtectedRoute>
                  <LazyRoute><MyBorrowsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/manage-books"
              element={
                <ProtectedRoute>
                  <LazyRoute><ManageBooksScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/manage-categories"
              element={
                <ProtectedRoute>
                  <LazyRoute><ManageCategoriesScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/borrow-requests"
              element={
                <ProtectedRoute>
                  <LazyRoute><BorrowRequestsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/records"
              element={
                <ProtectedRoute>
                  <LazyRoute><AllRecordsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/settings"
              element={
                <ProtectedRoute>
                  <LazyRoute><LibrarySettingsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/reports"
              element={
                <ProtectedRoute>
                  <LazyRoute><LibraryReportsScreen /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vehicles"
              element={
                <ProtectedRoute>
                  <LazyRoute><VehiclesDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <LazyRoute><UsersListPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/list"
              element={
                <ProtectedRoute>
                  <LazyRoute><UsersListPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/create"
              element={
                <ProtectedRoute>
                  <LazyRoute><CreateUserPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId/edit"
              element={
                <ProtectedRoute>
                  <LazyRoute><EditUserPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <ProtectedRoute>
                  <LazyRoute><UserDetailPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <LazyRoute><ProfileDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <LazyRoute><ReportsDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/target"
              element={
                <ProtectedRoute>
                  <LazyRoute><TargetDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/newsletter"
              element={
                <ProtectedRoute>
                  <LazyRoute><NewsletterDashboard /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/newsletter/create"
              element={
                <ProtectedRoute>
                  <LazyRoute><CreateNewsletterPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/newsletter/:id"
              element={
                <ProtectedRoute>
                  <LazyRoute><NewsletterDetailPage /></LazyRoute>
                </ProtectedRoute>
              }
            />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          
          {/* Toast notifications */}
          <Toast />
          </div>
        </InactivityProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
